import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI } from '../services/api';
import { showError } from '../utils/toast';
import { useTranslation } from 'react-i18next';

const DriverNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const driverEmail = localStorage.getItem('driverEmail');
      
      if (!driverEmail) {
        showError('❌ Please login as driver to view notifications');
        navigate('/');
        return;
      }

      const response = await notificationAPI.getNotifications(driverEmail);
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Error fetching driver notifications:', error);
      showError('❌ Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = (notification) => {
    if (notification.id) {
      navigate(`/driver/notification/${notification.id}`);
    }
  };

  const getNotificationTypeColor = (type) => {
    switch (type) {
      case 'ride_assignment': return 'bg-blue-100 text-blue-800';
      case 'booking_update': return 'bg-green-100 text-green-800';
      case 'payment_update': return 'bg-yellow-100 text-yellow-800';
      case 'system_alert': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading driver notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 pt-16 pb-20 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-12">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 px-3 py-2 sm:px-4 bg-white border border-yellow-200 rounded-lg hover:bg-yellow-50 transition-all text-sm sm:text-base"
          >
            ← {t('common.back') || 'Back'}
          </button>
          <h1 className="text-2xl sm:text-4xl font-bold text-center">
            <span className="bg-gradient-to-r from-gray-800 to-yellow-600 bg-clip-text text-transparent">
              🚗 {t('driver.notifications') || 'Driver Notifications'}
            </span>
          </h1>
        </div>

        {/* Notifications List */}
        <div className="space-y-4 sm:space-y-6">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className="bg-white/95 backdrop-blur-md rounded-xl sm:rounded-2xl border border-yellow-200 p-4 sm:p-6 shadow-lg cursor-pointer hover:shadow-xl transition-all hover:border-yellow-300"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 sm:mb-4 gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 break-words">
                    {notification.title}
                  </h3>
                  <div className="flex flex-col sm:items-end gap-2">
                    <span className="text-xs sm:text-sm text-gray-500 flex-shrink-0">
                      {new Date(notification.created_at).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getNotificationTypeColor(notification.notification_type)}`}>
                        {notification.notification_type?.replace('_', ' ').toUpperCase() || 'NOTIFICATION'}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        notification.is_read ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {notification.is_read ? 'READ' : 'NEW'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4">
                  <p className="text-gray-700 text-sm sm:text-base whitespace-pre-line break-words line-clamp-3">
                    {notification.message}
                  </p>
                </div>

                {notification.booking && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-yellow-600 mr-2">🚗</span>
                        <span className="font-semibold text-gray-800 text-sm sm:text-base">
                          Booking #{notification.booking}
                        </span>
                      </div>
                      <span className="text-blue-600 text-sm font-medium">
                        Click to view details →
                      </span>
                    </div>
                  </div>
                )}

                {!notification.is_read && (
                  <div className="mt-3 flex justify-end">
                    <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 sm:py-16">
              <div className="text-4xl sm:text-6xl mb-4">📭</div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                {t('driver.no_notifications') || 'No notifications yet'}
              </h3>
              <p className="text-gray-600 text-sm sm:text-base mb-6">
                {t('driver.no_notifications_desc') || 'You\'ll see ride assignments and updates here.'}
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-all font-semibold"
              >
                {t('common.go_home') || 'Go Home'}
              </button>
            </div>
          )}
        </div>

        {/* Refresh Button */}
        {notifications.length > 0 && (
          <div className="text-center mt-8">
            <button
              onClick={fetchNotifications}
              className="px-6 py-3 bg-white border border-yellow-200 rounded-lg hover:bg-yellow-50 transition-all font-semibold text-gray-700"
            >
              🔄 {t('common.refresh') || 'Refresh'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverNotifications;